import React, { useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { Alert } from "reactstrap";
import {
    APP_BODY_BACKGROUND,
    APP_SURFACE_BACKGROUND,
    getPrimarySurfaceSeparationTokens,
} from "src/services/utils/ThemeColorUtils";

const DEFAULT_PRIMARY = "#5664d2";
const DEFAULT_FONT = "#ffffff";

/**
 * Live preview of Primary Color + Font Color for Administration → Customization.
 * Visual demonstration only — not an actionable form control.
 * Reuses ThemeColorUtils so separation matches real primary buttons.
 */
const PrimaryColorPreview = ({
    t,
    primaryColor,
    fontColor,
    className = "",
}) => {
    const selectedPrimary = primaryColor || DEFAULT_PRIMARY;
    const selectedFont = fontColor || DEFAULT_FONT;

    const separation = useMemo(
        () => getPrimarySurfaceSeparationTokens(selectedPrimary),
        [selectedPrimary]
    );

    const previewSwatchStyle = {
        display: "inline-block",
        padding: "0.45rem 1.1rem",
        borderRadius: "0.25rem",
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.5,
        userSelect: "none",
        backgroundColor: selectedPrimary,
        color: selectedFont,
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: separation.borderColor,
        boxShadow: separation.surfaceShadow === "none"
            ? "none"
            : separation.surfaceShadow,
    };

    return (
        <div
            className={`customization-primary-color-preview ${className}`.trim()}
            aria-live="polite"
        >
            <div className="mb-1 fw-semibold">
                {t("Live Preview")}
            </div>
            <p className="text-muted small mb-2">
                {t("See how primary elements will appear with the selected colors.")}
            </p>

            <div
                className="rounded border p-3 d-flex align-items-center justify-content-center"
                style={{
                    backgroundColor: APP_BODY_BACKGROUND,
                    minHeight: 64,
                }}
            >
                <div
                    className="rounded px-3 py-2 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: APP_SURFACE_BACKGROUND }}
                >
                    <span
                        role="img"
                        aria-label={t("Primary Button")}
                        style={previewSwatchStyle}
                    >
                        {t("Primary Button")}
                    </span>
                </div>
            </div>

            {separation.needsSeparation && (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                    {t(
                        "This color has low contrast with the application background. A border will be applied automatically to keep primary elements visible."
                    )}
                </Alert>
            )}
        </div>
    );
};

export default withNamespaces()(PrimaryColorPreview);
